import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getOrders } from '../../api/endpoints/shop';

const statusColor = { pending: 'warning', confirmed: 'info', shipped: 'primary', delivered: 'success', cancelled: 'error', returned: 'default' };

const OrderList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter !== 'all') params.orderStatus = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getOrders(params);
      setOrders(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearch]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const columns = [
    { id: 'orderNumber', label: 'Order #', sortable: true, render: (row) => <Typography variant="body2" fontWeight={600} fontFamily="monospace">{row.orderNumber}</Typography> },
    { id: 'user', label: 'Customer', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.userId?.name || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">{row.userId?.phone || ''}</Typography>
      </Box>
    )},
    { id: 'items', label: 'Items', align: 'center', render: (row) => row.items?.length || 0 },
    { id: 'total', label: 'Total', align: 'right', render: (row) => <Typography variant="body2" fontWeight={600}>{formatCurrency(row.total)}</Typography> },
    { id: 'paymentStatus', label: 'Payment', render: (row) => <StatusChip status={row.paymentStatus} /> },
    { id: 'orderStatus', label: 'Status', render: (row) => <Chip label={row.orderStatus} size="small" color={statusColor[row.orderStatus] || 'default'} /> },
    { id: 'createdAt', label: 'Date', render: (row) => formatDateTime(row.createdAt) },
    { id: 'actions', label: '', render: (row) => (
      <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/shop/orders/${row._id}`)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Orders" subtitle="View and manage customer orders" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by order number..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {Object.keys(statusColor).map((s) => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable columns={columns} rows={orders} page={page} rowsPerPage={rowsPerPage} total={total}
          onPageChange={setPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} loading={loading}
          emptyMessage="No orders yet" />
      )}
    </Box>
  );
};

export default OrderList;
